import {
  COMMERCIAL_PROPERTY,
  CommercialProperty,
  OwnerOfTheBusiness,
  Listing,
  OWNER_OF_THE_BUSINESS,
} from 'models/Listing';
import get from 'lodash.get';
import { t } from 'i18next';
import {
  LESS_50_K,
  getAskingPrice,
  mapValueToMoneyFormat,
  dateFormat,
  checkIsUrgent,
} from 'utils';
import { STOCK_AT_VALUE } from 'models/DealRoom';

const BUSINESS_OWNER_TRANSLATION = {
  [OWNER_OF_THE_BUSINESS.WORK_IN_THE_BUSINESS]: 'workInTheBusinesUC',
  [OWNER_OF_THE_BUSINESS.SILENT]: 'isSilentOwner',
} as const;

export const COMMERCIAL_PROPERTY_TRANSLATION = {
  [COMMERCIAL_PROPERTY.SELLING]: 'areSelling',
  [COMMERCIAL_PROPERTY.NOT_SELLING]: 'areNotSellingUC',
  [COMMERCIAL_PROPERTY.WILL_OFFER]: 'optionToPurchaseCommercialPropertyUC',
};

const BUSINESS_INFO: Record<string, keyof Listing> = {
  businessDescription: 'businessDescription',
  highlights: 'highlights',
  'products/Services': 'products',
  keyAssets: 'keyAssets',
  businessSituation: 'type',
  location: 'location',
  locationPostcode: 'locationPostcode',
  industry: 'industry',
};

const STATEMENT_OF_INFORMATION: Record<string, string> = {
  revenue: 'revenue',
  ebitda: 'EBITDA',
  priceGuide: 'priceGuide',
  stockAtValuation: 'averageStockValue',
  ownerOfTheBusiness: 'ownerOfTheBusiness',
  isThereSomeoneInBusinessToReplace: 'someoneToReplaceOwner',
  ownersAlsoOwnCommercialProperty: 'commercialProperty',
};

export const SECTIONS: Record<string, Record<string, string>> = {
  businessInformation: BUSINESS_INFO,
  statementOfInformation: STATEMENT_OF_INFORMATION,
};

export const mappedInfo = <T extends object>(
  info: Record<string, string>,
  listing: T,
  isSuperAdmin: boolean,
  isSellerInformationSection: boolean,
): Record<string, string | string[] | number[]> =>
  isSellerInformationSection
    ? Object.entries(
        prepareData(info, listing as Listing, isSuperAdmin),
      ).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: getProperValue(key, value as string, listing as Listing),
        }),
        {},
      )
    : Object.entries(info).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: getProperValue(
            key,
            listing[value as keyof T],
            listing as Listing,
          ),
        }),
        {},
      );

const prepareData = (
  info: Record<string, string>,
  listing: Listing,
  isSuperAdmin: boolean,
) => {
  const objectPrefix = 'listingContact';

  return Object.entries(info).reduce((acc, [key, value]) => {
    if (
      value === 'campaignExpires' &&
      (!isSuperAdmin || !checkIsUrgent(listing.type))
    ) {
      return { ...acc };
    }

    if (value === 'company') {
      return {
        ...acc,
        [key]: listing?.[objectPrefix]?.[value]?.['name'],
      };
    }

    return {
      ...acc,
      [key]: get(listing, [objectPrefix, value]),
    };
  }, {});
};

const getProperValue = <T extends object>(
  key: string,
  value: T[keyof T] | string,
  listing: Listing,
) => {
  if (key === 'ownerOfTheBusiness' && value)
    return t(BUSINESS_OWNER_TRANSLATION[value as OwnerOfTheBusiness]);
  if (key === 'isThereSomeoneInBusinessToReplace')
    return value ? t('yes') : t('no');
  if (key === 'ownersAlsoOwnCommercialProperty' && value)
    return t(COMMERCIAL_PROPERTY_TRANSLATION[value as CommercialProperty]);

  if (key === 'askingPricePriceRange')
    return +value
      ? { amountFrom: listing.amountFrom, amountTo: listing.amountTo }
      : { amount: listing.amount };

  if (key === 'location')
    return listing.location?.state || listing.dealLocation;

  if (key === 'stockAtValuation') {
    if (
      !listing?.averageStockValue ||
      listing?.stockAtValue === STOCK_AT_VALUE.NO_STOCK_AT_VALUE
    )
      return '';
    const priceGuideSufix =
      listing?.stockAtValue === STOCK_AT_VALUE.INCLUDE_STOCK_AT_VALUE
        ? t('includedInPriceGuide')
        : t('excludedInPriceGuide');

    const value = mapValueToMoneyFormat(listing?.averageStockValue);
    let text = value;
    if (value === LESS_50_K) {
      text = `${value} (${t('approximately')})`;
    }

    return `${text} (${priceGuideSufix})`;
  }
  if (key === 'priceGuide') {
    return getAskingPrice(listing, true);
  }
  if (key === 'campaignExpires') {
    if (!listing.seekingExpressionOfInterestDate && !listing.expirationDate)
      return '';

    if (!listing.expirationDate)
      return dateFormat(new Date(listing?.seekingExpressionOfInterestDate));

    if (!listing.seekingExpressionOfInterestDate)
      return dateFormat(new Date(listing?.expirationDate));

    const seekingDate = new Date(listing.seekingExpressionOfInterestDate);
    const exprDate = new Date(listing.expirationDate);

    return dateFormat(seekingDate < exprDate ? seekingDate : exprDate);
  }
  return value;
};
